"use client";

import { useRef, useEffect } from 'react';
import { Search, Calendar, UserCheck, CreditCard, Star, ArrowRight } from 'lucide-react';

const howSortkaroWorksSteps = [
  {
    index: 1,
    icon: Search,
    title: "Choose a Service",
    desc: "Browse through our wide range of home services like cleaning, kitchen care, bathroom cleaning, and more. Select what fits your needs.",
    color: "bg-blue-50",
    iconColor: "text-blue-600"
  },
  {
    index: 2,
    icon: UserCheck,
    title: "Share Your Details",
    desc: "Tell us your location, preferred date, and time. This helps us assign the right professional near you.",
    color: "bg-green-50",
    iconColor: "text-green-600"
  },
  {
    index: 3,
    icon: Calendar,
    title: "Schedule Easily",
    desc: "Pick a time slot that works best for you. Our system is designed to be quick and hassle-free.",
    color: "bg-purple-50",
    iconColor: "text-purple-600"
  },
  {
    index: 4,
    icon: CreditCard,
    title: "Secure Payment",
    desc: "Pay safely using UPI, cards, or other trusted payment methods. No hidden charges—100% transparent pricing.",
    color: "bg-yellow-50",
    iconColor: "text-yellow-600"
  },
  {
    index: 5,
    icon: Star,
    title: "Sit Back & Relax",
    desc: "Our trained and verified professionals arrive on time and deliver high-quality service at your doorstep.",
    color: "bg-teal-50",
    iconColor: "text-teal-600"
  },
  {
    index: 6,
    icon: Star,
    title: "Rate & Review",
    desc: "After the service, share your feedback. Your reviews help us improve and guide other customers.",
    color: "bg-orange-50",
    iconColor: "text-orange-600"
  }
];

export default function HowSortkaroWorks() {
  const scrollRef = useRef(null);

  // Triple the steps for seamless infinite scroll
  const infiniteSteps = [...howSortkaroWorksSteps, ...howSortkaroWorksSteps, ...howSortkaroWorksSteps];

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationId;
    let scrollPosition = 0;
    const cardWidth = 360; // card width + gap
    const totalWidth = howSortkaroWorksSteps.length * cardWidth;

    const scroll = () => {
      scrollPosition += 0.5; // Smooth scroll speed

      if (scrollPosition >= totalWidth) {
        scrollPosition = 0;
      }

      scrollContainer.scrollLeft = scrollPosition;
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-extrabold text-gray-900 mb-3">
            How SortKaro Works
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed mb-2">
            <span className="font-bold text-gray-900">Simple. Fast. Reliable.</span>
          </p>
          <p className="text-gray-600 text-base max-w-3xl mx-auto leading-relaxed">
            SortKaro makes booking home services effortless. Just follow these easy steps and relax while we take care of the rest.
          </p>
        </div>

        {/* Horizontal Scrolling Steps */}
        <div className="relative">
          {/* Gradient Overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

          {/* Scrollable Container */}
          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-hidden scrollbar-hide py-4"
            style={{ scrollBehavior: 'auto' }}
          >
            {infiniteSteps.map((step, index) => {
              const Icon = step.icon;
              const isNotLast = (index % howSortkaroWorksSteps.length) !== (howSortkaroWorksSteps.length - 1);

              return (
                <div key={index} className="relative flex items-center flex-shrink-0">
                  {/* Step Card */}
                  <div
                    className={`
                      min-w-[340px] max-w-[340px]
                      bg-white rounded-2xl p-6 
                      border border-gray-100
                      shadow-[0_2px_8px_rgba(0,0,0,0.04)]
                      hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)]
                      hover:-translate-y-1
                      transition-all duration-300 ease-out
                      cursor-pointer
                    `}
                  >
                    {/* Step Number & Icon */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00489b] to-[#0066cc] text-white flex items-center justify-center font-bold text-lg shadow-md">
                          {step.index}
                        </div>
                      </div>
                      <div className={`${step.color} w-12 h-12 rounded-xl flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 ${step.iconColor}`} />
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                      {step.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {step.desc}
                    </p>
                  </div>

                  {/* Arrow Connector (only between cards, not after last) */}
                  {isNotLast && (
                    <div className="flex-shrink-0 mx-2">
                      <ArrowRight className="w-6 h-6 text-gray-300" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
